import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { AbstractComponent } from '../../abstract.component';
import { SolImage } from '../../../models/core/sol-image';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { DialogService } from '../../../services/dialog.service';
import { SolImageDialogComponent } from '../../dialog/sol-image-dialog/sol-image-dialog.component';
import { SolIconComponent } from '../sol-icon/sol-icon.component';

@Component({
  selector: 'sol-image',
  standalone: true,
  imports: [SolIconComponent, TranslatePipe],
  templateUrl: './sol-image.component.html',
  host: {
    '[class.clickable]': 'clickable$()',
    '[class.sol-image-fill]': 'fill$()',
    '[style.--sol-image-width]': "styleVars$()['--sol-image-width']",
    '[style.--sol-image-height]': "styleVars$()['--sol-image-height']",
    '[style.--sol-image-max-width]': "styleVars$()['--sol-image-max-width']",
    '[style.--sol-image-max-height]': "styleVars$()['--sol-image-max-height']",
    '[style.--sol-image-object-fit]': "styleVars$()['--sol-image-object-fit']",
    '[style.--sol-image-caption-max-width]': "styleVars$()['--sol-image-caption-max-width']",
    '[style.--sol-image-placeholder-color]': "styleVars$()['--sol-image-placeholder-color']",
    '[style.--sol-image-placeholder-background]': "styleVars$()['--sol-image-placeholder-background']",
  },
})
export class SolImageComponent extends AbstractComponent<SolImage> {
  // Dependencies
  private readonly dialogService = inject(DialogService);

  // Events
  @Output('imageClick')
  public readonly imageClickEmitter = new EventEmitter<SolImage>();

  // Computed
  protected readonly styleVars$ = this.compute(image => image.styleVars);
  protected readonly src$ = this.compute(image => image.src);
  protected readonly alt$ = this.compute(image => image.alt);
  protected readonly caption$ = this.compute(image => image.caption);
  protected readonly placeholder$ = this.compute(image => image.placeholder);
  protected readonly showPlaceholder$ = this.compute(image => image.showPlaceholder);
  protected readonly clickable$ = this.compute(image => image.clickBehavior !== 'default');
  protected readonly fill$ = this.compute(image => image.width === 'fill');

  constructor() {
    super(SolImage.empty());
  }

  @Input()
  public set image(image: SolImage | undefined) {
    if (image !== undefined) {
      this.patch(image);
    }
  }

  protected onClick(): void {
    const behavior = this.state.clickBehavior;
    if (behavior === 'fullScreen') {
      this.showFullScreen();
    } else if (behavior === 'click') {
      this.imageClickEmitter.emit(this.state);
    }
  }

  protected onLoadError(): void {
    this.update(image => image.withLoadFailed(true));
  }

  protected onKeyActivate(event: Event): void {
    if (this.state.clickBehavior === 'default') {
      return;
    }
    event.preventDefault();
    this.onClick();
  }

  private showFullScreen(): void {
    if (!this.state.src) {
      return;
    }
    this.dialogService.showDialog(SolImageDialogComponent, instance => (instance.image = this.state));
  }
}
